import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { LogOut, Settings, BarChart3, Package, Layers, Edit, Mail, Trash2, Bot, Loader2, Image, Youtube, ChevronUp, ChevronDown, Upload, Megaphone, RefreshCw } from 'lucide-react';
import OfferTranslationModal from '../components/OfferTranslationModal';
import AdminPromoBarTab, { mergePromoBarFromApi, DEFAULT_PROMO_BAR_CONFIG } from '../components/AdminPromoBarTab';
import AdminMatomoTab, { mergeMatomoFromApi, DEFAULT_MATOMO_CONFIG } from '../components/AdminMatomoTab';
import AdminGoogleAnalyticsTab, {
  mergeGoogleAnalyticsFromApi,
  DEFAULT_GOOGLE_ANALYTICS_CONFIG,
} from '../components/AdminGoogleAnalyticsTab';
import AdminWowBookingTab, { DEFAULT_WOWBOOKING_CONFIG } from '../components/AdminWowBookingTab';
import AdminSidebar from '../components/admin/AdminSidebar';
import SeoHead from '../components/SeoHead';

const wbOfferId = (o) => {
  const n = Number(o?.id);
  return Number.isNaN(n) ? null : n;
};

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const [leads, setLeads] = useState([]);
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState(null);
  const [newConfigKey, setNewConfigKey] = useState('');
  const [newConfigValue, setNewConfigValue] = useState('');
  const [newConfigDesc, setNewConfigDesc] = useState('');
  const [allOffers, setAllOffers] = useState([]);
  const [visibleOffers, setVisibleOffers] = useState([]);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false);
  const [stripeConfig, setStripeConfig] = useState({
    mode: 'test',
    test_publishable_key: '',
    test_secret_key: '',
    test_webhook_secret: '',
    live_publishable_key: '',
    live_secret_key: '',
    live_webhook_secret: ''
  });
  const [stripeVerifyLoading, setStripeVerifyLoading] = useState(false);
  const [stripeVerifyResult, setStripeVerifyResult] = useState(null);
  const [wowbookingConfig, setWowbookingConfig] = useState(() => ({
    ...DEFAULT_WOWBOOKING_CONFIG,
    mode: typeof window !== 'undefined' && window.location.hostname.startsWith('dev.')
      ? 'dev'
      : 'production',
  }));
  const [wowbookingVerifyLoading, setWowbookingVerifyLoading] = useState(false);
  const [wowbookingVerifyResult, setWowbookingVerifyResult] = useState(null);
  const [wowbookingSource, setWowbookingSource] = useState(null);
  const [wowbookingOffersError, setWowbookingOffersError] = useState(null);
  const [offersReloading, setOffersReloading] = useState(false);
  const [sendgridConfig, setSendgridConfig] = useState({
    api_key: '',
    from_email: 'noreply@wownautic.com',
    to_email: 'info@wownautic.com',
    contact_notification_cc: ''
  });
  const [contactMessages, setContactMessages] = useState([]);
  const [contactMessageDeleteId, setContactMessageDeleteId] = useState(null);
  const [offerSlugs, setOfferSlugs] = useState([]);
  const [categoriesCatalog, setCategoriesCatalog] = useState([]);
  const [newCategory, setNewCategory] = useState({ name: '', slug: '', names: { fr: '', en: '', es: '' } });
  const [offerCategorySearch, setOfferCategorySearch] = useState('');
  const [offerSlugSearch, setOfferSlugSearch] = useState('');
  const [offerCategoryDrafts, setOfferCategoryDrafts] = useState({});
  const [offerSlugDrafts, setOfferSlugDrafts] = useState({});
  const [newSlug, setNewSlug] = useState({ offer_id: '', slug: '', primary_category: '' });
  const [totemiumConfig, setTotemiumConfig] = useState({ api_base_url: '', api_key: '', agent_id: null, agent_name: '', agent_icon: '', translator_agent_id: null, translator_agent_name: '', translator_agent_icon: '' });
  const [totemiumAgents, setTotemiumAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [heroMedia, setHeroMedia] = useState({ type: 'image', image_url: '/images/Doqueve.jpg', youtube_url: '', start_time: null, end_time: null, play_once: false, play_on_mobile: true, fallback_image: '/images/Doqueve.jpg' });
  const [promoBarConfig, setPromoBarConfig] = useState(() => ({
    ...DEFAULT_PROMO_BAR_CONFIG,
    custom: { ...DEFAULT_PROMO_BAR_CONFIG.custom },
  }));
  const [matomoConfig, setMatomoConfig] = useState({ ...DEFAULT_MATOMO_CONFIG });
  const [googleAnalyticsConfig, setGoogleAnalyticsConfig] = useState({
    ...DEFAULT_GOOGLE_ANALYTICS_CONFIG,
  });
  const [bundles, setBundles] = useState([]);
  const [visibleBundles, setVisibleBundles] = useState([]);
  const [bundleForm, setBundleForm] = useState({
    slug: '',
    primary_category: 'boat-tours',
    display_title: '',
    featured_image_url: '',
    hide_children_on_homepage: true,
  });
  const [bundleVariantOrder, setBundleVariantOrder] = useState([]);
  const [bundleOfferSearch, setBundleOfferSearch] = useState('');
  const [bundleImageUploading, setBundleImageUploading] = useState(false);
  const [bundleImageDragActive, setBundleImageDragActive] = useState(false);
  const bundleFileInputRef = useRef(null);

  const filteredOffersForBundle = useMemo(() => {
    const q = bundleOfferSearch.trim().toLowerCase();
    const list = [...allOffers].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'fr'));
    if (!q) return list;
    return list.filter((o) => {
      const idStr = String(wbOfferId(o) ?? '');
      const title = (o.title || '').toLowerCase();
      return idStr.includes(q) || title.includes(q);
    });
  }, [allOffers, bundleOfferSearch]);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const stripeWebhookUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/api/stripe/webhook`
      : 'https://wownautic.com/api/stripe/webhook';
  const isDevBackoffice =
    typeof window !== 'undefined' && window.location.hostname.startsWith('dev.');

  useEffect(() => {
    if (token) {
      verifyToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const slugifyValue = (value) => {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s_-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const getNormalizedSlugMeta = (offer, slugRecord) => {
    const defaultSlug = slugifyValue(offer?.title || `offer-${offer?.id || 'x'}`);
    const slug = slugRecord?.slug || defaultSlug;
    const categories = Array.isArray(slugRecord?.categories)
      ? slugRecord.categories.filter(Boolean)
      : (slugRecord?.category ? [slugRecord.category] : []);
    let primary = slugRecord?.primary_category || slugRecord?.category || '';
    if (!primary && categories.length) primary = categories[0];
    if (primary && !categories.includes(primary)) categories.push(primary);
    return { slug, categories, primary_category: primary };
  };

  const verifyToken = async () => {
    const currentToken = (localStorage.getItem('admin_token') || '').trim();
    if (!currentToken) {
      setIsAuthenticated(false);
      return;
    }
    try {
      await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      setIsAuthenticated(true);
      loadData();
    } catch (error) {
      localStorage.removeItem('admin_token');
      setToken(null);
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/login`, {
        username: username.trim(),
        password,
      });
      const { access_token } = response.data;
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setIsAuthenticated(true);
      toast.success('Login successful');
      loadData();
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };

  const loadWowbookingOffers = async ({ showToast = false } = {}) => {
    const currentToken = localStorage.getItem('admin_token');
    if (!currentToken) return false;

    setOffersReloading(true);
    setWowbookingOffersError(null);
    try {
      const offersRes = await axios.get(`${API_URL}/api/admin/wowbooking-offers`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const offers = offersRes.data.offers || [];
      setAllOffers(offers);
      setWowbookingSource(offersRes.data.source || null);

      const slugsRes = await axios.get(`${API_URL}/api/admin/offer-slugs`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => ({ data: { slugs: [] } }));
      const visibleOffersRes = await axios.get(`${API_URL}/api/admin/visible-offers`, {
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => ({ data: { visible_offers: [] } }));

      setVisibleOffers(visibleOffersRes.data.visible_offers || []);
      if (visibleOffersRes.data.wowbooking_mode) {
        setWowbookingSource((prev) => ({
          ...(prev || {}),
          mode: visibleOffersRes.data.wowbooking_mode,
          api_url: visibleOffersRes.data.wowbooking_api_url,
          tenant_slug: visibleOffersRes.data.wowbooking_tenant_slug,
          count: offers.length,
        }));
      }

      const slugMap = new Map((slugsRes.data.slugs || []).map((row) => [row.offer_id, row]));
      const nextCategoryDrafts = {};
      const nextSlugDrafts = {};
      offers.forEach((offer) => {
        const normalized = getNormalizedSlugMeta(offer, slugMap.get(offer.id));
        nextCategoryDrafts[offer.id] = {
          categories: [...normalized.categories],
          primary_category: normalized.primary_category,
          slug: normalized.slug,
        };
        nextSlugDrafts[offer.id] = {
          slug: normalized.slug,
          primary_category: normalized.primary_category,
        };
      });
      setOfferCategoryDrafts(nextCategoryDrafts);
      setOfferSlugDrafts(nextSlugDrafts);

      if (showToast) {
        toast.success(`${offers.length} offre(s) chargée(s) depuis WowBooking`);
      }
      return true;
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Impossible de charger les offres WowBooking';
      setWowbookingOffersError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setAllOffers([]);
      setWowbookingSource(null);
      if (showToast) {
        toast.error(typeof detail === 'string' ? detail : 'Erreur API WowBooking');
      }
      return false;
    } finally {
      setOffersReloading(false);
    }
  };

  const loadData = async () => {
    const currentToken = localStorage.getItem('admin_token');
    if (!currentToken) {
      toast.error('No authentication token');
      return;
    }
    
    try {
      const [leadsRes, configsRes, statsRes, visibleOffersRes, stripeConfigRes, slugsRes, categoriesRes, bundlesRes, visibleBundlesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/leads`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }),
        axios.get(`${API_URL}/api/admin/config`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }),
        axios.get(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }),
        axios.get(`${API_URL}/api/admin/visible-offers`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }),
        axios.get(`${API_URL}/api/admin/stripe-config`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/admin/offer-slugs`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }).catch(() => ({ data: { slugs: [] } })),
        axios.get(`${API_URL}/api/admin/categories`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }).catch(() => ({ data: { categories: [] } })),
        axios.get(`${API_URL}/api/admin/offer-bundles`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }).catch(() => ({ data: { bundles: [] } })),
        axios.get(`${API_URL}/api/admin/visible-bundles`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        }).catch(() => ({ data: { visible_bundles: [] } }))
      ]);
      
      setLeads(leadsRes.data.leads || []);
      setConfigs(Array.isArray(configsRes.data) ? configsRes.data : (configsRes.data.configs || []));
      setStats(statsRes.data);
      setVisibleOffers(visibleOffersRes.data.visible_offers || visibleOffersRes.data.value || []);
      setOfferSlugs(slugsRes.data.slugs || []);
      setCategoriesCatalog(categoriesRes.data.categories || []);
      setBundles(bundlesRes.data.bundles || []);
      setVisibleBundles(visibleBundlesRes.data.visible_bundles || []);

      if (visibleOffersRes.data.wowbooking_mode) {
        setWowbookingSource({
          mode: visibleOffersRes.data.wowbooking_mode,
          api_url: visibleOffersRes.data.wowbooking_api_url,
          tenant_slug: visibleOffersRes.data.wowbooking_tenant_slug,
        });
      }
      
      if (stripeConfigRes.data) {
        // Load Stripe config, keeping existing values for secret fields if not provided
        const newConfig = {
          mode: stripeConfigRes.data.mode || 'test',
          test_publishable_key: stripeConfigRes.data.test_publishable_key || '',
          test_secret_key: stripeConfigRes.data.test_secret_key || '',
          test_webhook_secret: stripeConfigRes.data.test_webhook_secret || '',
          live_publishable_key: stripeConfigRes.data.live_publishable_key || '',
          live_secret_key: stripeConfigRes.data.live_secret_key || '',
          live_webhook_secret: stripeConfigRes.data.live_webhook_secret || ''
        };
        setStripeConfig(newConfig);
      }

      try {
        const wbRes = await axios.get(`${API_URL}/api/admin/wowbooking-config`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setWowbookingConfig({
          ...DEFAULT_WOWBOOKING_CONFIG,
          mode: isDevBackoffice ? 'dev' : 'production',
          ...(wbRes.data || {}),
        });
      } catch (error) {
        console.log('Failed to load WowBooking config, using defaults');
        setWowbookingConfig({
          ...DEFAULT_WOWBOOKING_CONFIG,
          mode: isDevBackoffice ? 'dev' : 'production',
        });
      }
      
      // Load SendGrid config separately
      try {
        const sendgridConfigRes = await axios.get(`${API_URL}/api/admin/sendgrid-config`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setSendgridConfig({
          api_key: '',
          from_email: 'noreply@wownautic.com',
          to_email: 'info@wownautic.com',
          contact_notification_cc: '',
          ...(sendgridConfigRes.data || {}),
          contact_notification_cc:
            (sendgridConfigRes.data || {}).contact_notification_cc ?? ''
        });
      } catch (error) {
        console.log('Failed to load SendGrid config, using defaults');
        setSendgridConfig({
          api_key: '',
          from_email: 'noreply@wownautic.com',
          to_email: 'info@wownautic.com',
          contact_notification_cc: ''
        });
      }
      
      // Load contact messages separately
      try {
        const contactMessagesRes = await axios.get(`${API_URL}/api/admin/contact-messages`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setContactMessages(contactMessagesRes.data || []);
      } catch (error) {
        console.log('Failed to load contact messages');
        setContactMessages([]);
      }
      
      // Load newsletter subscribers
      try {
        const newsletterRes = await axios.get(`${API_URL}/api/admin/newsletter`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setNewsletterSubscribers(newsletterRes.data || []);
      } catch (error) {
        console.log('Failed to load newsletter subscribers');
        setNewsletterSubscribers([]);
      }
      
      // Load Hero media config
      try {
        const heroMediaRes = await axios.get(`${API_URL}/api/admin/hero-media`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        const hm = heroMediaRes.data || {};
        setHeroMedia({
          type: hm.type || 'image',
          image_url: hm.image_url || hm.value || '/images/Doqueve.jpg',
          youtube_url: hm.youtube_url || (hm.type === 'youtube' ? (hm.value || '') : ''),
          start_time: hm.start_time ?? null,
          end_time: hm.end_time ?? null,
          play_once: hm.play_once || false,
          play_on_mobile: hm.play_on_mobile !== false,
          fallback_image: hm.fallback_image || '/images/Doqueve.jpg',
        });
      } catch (error) {
        console.log('Failed to load hero media config');
      }

      try {
        const promoBarRes = await axios.get(`${API_URL}/api/admin/promo-bar`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setPromoBarConfig(mergePromoBarFromApi(promoBarRes.data));
      } catch (error) {
        console.log('Failed to load promo bar config');
      }

      try {
        const matomoRes = await axios.get(`${API_URL}/api/admin/matomo-config`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setMatomoConfig(mergeMatomoFromApi(matomoRes.data));
      } catch (error) {
        console.log('Failed to load Matomo config');
      }

      try {
        const gaRes = await axios.get(`${API_URL}/api/admin/google-analytics-config`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setGoogleAnalyticsConfig(mergeGoogleAnalyticsFromApi(gaRes.data));
      } catch (error) {
        console.log('Failed to load Google Analytics config');
      }

      // Load Totemium config
      try {
        const totemiumRes = await axios.get(`${API_URL}/api/admin/totemium-config`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setTotemiumConfig(totemiumRes.data || { api_base_url: '', api_key: '', agent_id: null, agent_name: '', agent_icon: '', translator_agent_id: null, translator_agent_name: '', translator_agent_icon: '' });
      } catch (error) {
        console.log('Failed to load Totemium config');
      }

      await loadWowbookingOffers();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.patch(
        `${API_URL}/api/admin/leads/${leadId}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      toast.success('Lead updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const addOrUpdateConfig = async () => {
    if (!newConfigKey || !newConfigValue) {
      toast.error('Key and value are required');
      return;
    }

    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(
        `${API_URL}/api/admin/config`,
        {
          key: newConfigKey,
          value: newConfigValue,
          description: newConfigDesc
        },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      toast.success('Configuration saved');
      setNewConfigKey('');
      setNewConfigValue('');
      setNewConfigDesc('');
      loadData();
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  const deleteConfig = async (key) => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.delete(
        `${API_URL}/api/admin/config/${key}`,
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      toast.success('Configuration deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const toggleOfferVisibility = async (offerId) => {
    const currentToken = localStorage.getItem('admin_token');
    const newVisibleOffers = visibleOffers.includes(offerId)
      ? visibleOffers.filter(id => id !== offerId)
      : [...visibleOffers, offerId];
    
    try {
      await axios.post(
        `${API_URL}/api/admin/visible-offers`,
        newVisibleOffers,
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      setVisibleOffers(newVisibleOffers);
      toast.success('Offer visibility updated');
    } catch (error) {
      toast.error('Failed to update offer visibility');
    }
  };

  const toggleBundleVisibility = async (slug) => {
    const currentToken = localStorage.getItem('admin_token');
    const next = visibleBundles.includes(slug)
      ? visibleBundles.filter((s) => s !== slug)
      : [...visibleBundles, slug];
    try {
      await axios.post(
        `${API_URL}/api/admin/visible-bundles`,
        next,
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      setVisibleBundles(next);
      toast.success('Bundle visibility updated');
    } catch (error) {
      toast.error('Failed to update bundle visibility');
    }
  };

  const toggleBundleVariant = (offerId) => {
    const id = Number(offerId);
    if (Number.isNaN(id)) return;
    setBundleVariantOrder((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };

  const moveBundleVariant = (index, delta) => {
    setBundleVariantOrder((prev) => {
      const j = index + delta;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const uploadBundleFeaturedImage = async (file) => {
    if (!file) return;
    const okType = /^image\/(jpeg|png|webp)$/i.test(file.type || '');
    if (!okType) {
      toast.error('Formats acceptés : JPEG, PNG, WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }
    const currentToken = localStorage.getItem('admin_token');
    const fd = new FormData();
    fd.append('file', file);
    setBundleImageUploading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/upload/bundle-image`, fd, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const url = res.data?.url;
      if (url) {
        setBundleForm((f) => ({ ...f, featured_image_url: url }));
        toast.success('Image enregistrée');
      } else {
        toast.error('Réponse serveur invalide');
      }
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Échec du téléversement');
    } finally {
      setBundleImageUploading(false);
    }
  };

  const createOfferBundle = async () => {
    const currentToken = localStorage.getItem('admin_token');
    const ids = bundleVariantOrder.filter((n) => typeof n === 'number' && !Number.isNaN(n) && n > 0);
    const slug = slugifyValue(bundleForm.slug);
    if (!slug || ids.length === 0) {
      toast.error('Indiquez un slug et sélectionnez au moins une offre');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/admin/offer-bundles`,
        {
          slug,
          primary_category: bundleForm.primary_category.trim() || 'boat-tours',
          variant_offer_ids: ids,
          display_title: bundleForm.display_title.trim() || null,
          featured_image_url: bundleForm.featured_image_url.trim() || null,
          hide_children_on_homepage: bundleForm.hide_children_on_homepage,
        },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      toast.success('Bundle created');
      setBundleForm({
        slug: '',
        primary_category: 'boat-tours',
        display_title: '',
        featured_image_url: '',
        hide_children_on_homepage: true,
      });
      setBundleVariantOrder([]);
      setBundleOfferSearch('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create bundle');
    }
  };

  const deleteOfferBundle = async (slug) => {
    if (!window.confirm(`Delete bundle "${slug}"?`)) return;
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API_URL}/api/admin/offer-bundles/${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Bundle deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bundle');
    }
  };

  const openTranslationModal = (offer) => {
    setSelectedOffer(offer);
    setIsTranslationModalOpen(true);
  };

  const closeTranslationModal = () => {
    setSelectedOffer(null);
    setIsTranslationModalOpen(false);
  };

  const handleTranslationSave = () => {
    loadData();
  };

  const handleSaveStripeConfig = async () => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API_URL}/api/admin/stripe-config`, stripeConfig, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Stripe configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save Stripe configuration');
      console.error(error);
    }

    await loadWowbookingOffers();
  };

  const handleSaveWowbookingConfig = async () => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API_URL}/api/admin/wowbooking-config`, wowbookingConfig, {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      toast.success('Configuration WowBooking enregistrée');
      await loadWowbookingOffers({ showToast: true });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Échec de l’enregistrement WowBooking');
      console.error(error);
    }
  };

  const handleVerifyWowbookingConnection = async () => {
    const currentToken = localStorage.getItem('admin_token');
    setWowbookingVerifyLoading(true);
    setWowbookingVerifyResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/wowbooking-config/verify`,
        { mode: wowbookingConfig.mode },
        { headers: { Authorization: `Bearer ${currentToken}` } }
      );
      setWowbookingVerifyResult(response.data);
      toast.success('Connexion WowBooking vérifiée');
      await loadWowbookingOffers({ showToast: true });
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Échec de la vérification';
      setWowbookingVerifyResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setWowbookingVerifyLoading(false);
    }
  };

  const handleVerifyStripeConnection = async () => {
    const currentToken = localStorage.getItem('admin_token');
    setStripeVerifyLoading(true);
    setStripeVerifyResult(null);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/stripe-config/verify`,
        {
          mode: stripeConfig.mode,
          webhook_url: stripeWebhookUrl,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` }
        }
      );
      setStripeVerifyResult(response.data);

      const endpointFound = response.data?.webhook_endpoint_found;
      if (endpointFound === false) {
        toast.warning('Stripe connected, but webhook endpoint was not found in Stripe Dashboard');
      } else {
        toast.success('Stripe connection verified successfully');
      }
    } catch (error) {
      setStripeVerifyResult({
        ok: false,
        error: error?.response?.data?.detail || 'Stripe verification failed',
      });
      toast.error(error?.response?.data?.detail || 'Stripe verification failed');
    } finally {
      setStripeVerifyLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategory.name.trim();
    const slug = slugifyValue(newCategory.slug || name);
    if (!name || !slug) {
      toast.error('Category name is required');
      return;
    }
    const names = {};
    if (newCategory.names?.fr?.trim()) names.fr = newCategory.names.fr.trim();
    if (newCategory.names?.en?.trim()) names.en = newCategory.names.en.trim();
    if (newCategory.names?.es?.trim()) names.es = newCategory.names.es.trim();
    if (!names.fr) names.fr = name;

    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API_URL}/api/admin/categories`, {
        name,
        slug,
        names,
        order: 0,
        is_active: true
      }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setNewCategory({ name: '', slug: '', names: { fr: '', en: '', es: '' } });
      toast.success('Catégorie créée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleUpdateCategoryNames = async (categorySlug, names) => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.patch(`${API_URL}/api/admin/categories/${encodeURIComponent(categorySlug)}`, {
        names
      }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Traductions mises à jour');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteCategory = async (categorySlug) => {
    if (!window.confirm(`Delete category "${categorySlug}"?`)) return;
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API_URL}/api/admin/categories/${encodeURIComponent(categorySlug)}`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Category deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    }
  };

  const toggleOfferCategory = (offerId, categorySlug) => {
    setOfferCategoryDrafts((prev) => {
      const current = prev[offerId] || { categories: [], primary_category: '', slug: slugifyValue(`offer-${offerId}`) };
      const isSelected = current.categories.includes(categorySlug);
      const categories = isSelected
        ? current.categories.filter((c) => c !== categorySlug)
        : [...current.categories, categorySlug];
      let primary = current.primary_category;
      if (!categories.length) {
        primary = '';
      } else if (!primary || !categories.includes(primary)) {
        primary = categories[0];
      }
      return {
        ...prev,
        [offerId]: { ...current, categories, primary_category: primary }
      };
    });
  };

  const handleSaveOfferCategories = async (offer) => {
    const draft = offerCategoryDrafts[offer.id];
    if (!draft || !Array.isArray(draft.categories) || draft.categories.length === 0) {
      toast.error('Select at least one category');
      return;
    }
    const primary = draft.categories.includes(draft.primary_category) ? draft.primary_category : draft.categories[0];
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API_URL}/api/admin/offer-slugs`, {
        offer_id: offer.id,
        slug: draft.slug || slugifyValue(offer.title || `offer-${offer.id}`),
        categories: draft.categories,
        primary_category: primary
      }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Offer categories saved');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save offer categories');
    }
  };

  const handleCreateSlug = async () => {
    if (!newSlug.offer_id || !newSlug.slug) {
      toast.error('Offer ID and slug are required');
      return;
    }

    const currentToken = localStorage.getItem('admin_token');
    try {
      const offerId = parseInt(newSlug.offer_id);
      const categoryDraft = offerCategoryDrafts[offerId];
      const categories = (categoryDraft?.categories && categoryDraft.categories.length > 0)
        ? categoryDraft.categories
        : (newSlug.primary_category ? [newSlug.primary_category] : []);
      const primary = newSlug.primary_category || categoryDraft?.primary_category || categories[0] || '';
      await axios.post(`${API_URL}/api/admin/offer-slugs`, {
        offer_id: offerId,
        slug: slugifyValue(newSlug.slug),
        categories,
        primary_category: primary
      }, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Slug created successfully');
      setNewSlug({ offer_id: '', slug: '', primary_category: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create slug');
    }
  };

  const handleUpdateSlug = async (offerId, updates) => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      const categoryDraft = offerCategoryDrafts[offerId] || { categories: [], primary_category: '' };
      const payload = {
        ...updates,
        categories: categoryDraft.categories,
        primary_category: updates.primary_category || categoryDraft.primary_category || categoryDraft.categories[0] || ''
      };
      await axios.patch(`${API_URL}/api/admin/offer-slugs/${offerId}`, payload, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Slug updated successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update slug');
    }
  };

  const handleDeleteSlug = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this slug?')) {
      return;
    }

    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API_URL}/api/admin/offer-slugs/${offerId}`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Slug deleted successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to delete slug');
    }
  };

  const handleSaveTotemiumConfig = async () => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API_URL}/api/admin/totemium-config`, totemiumConfig, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Totemium configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save Totemium configuration');
      console.error(error);
    }
  };

  const handleLoadTotemiumAgents = async () => {
    const currentToken = localStorage.getItem('admin_token');
    setLoadingAgents(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/totemium-agents`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      const agents = res.data?.items || res.data || [];
      setTotemiumAgents(agents);
      if (agents.length === 0) {
        toast.info('No agents found for this API key');
      } else {
        toast.success(`${agents.length} agent(s) loaded`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to load agents';
      toast.error(detail);
      setTotemiumAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleSelectTotemiumAgent = (agent) => {
    setTotemiumConfig({
      ...totemiumConfig,
      agent_id: agent.id,
      agent_name: agent.name,
      agent_icon: agent.icon_url || ''
    });
    toast.info(`Agent "${agent.name}" selected — click Save to confirm`);
  };

  const handleSelectTranslatorAgent = (agent) => {
    setTotemiumConfig({
      ...totemiumConfig,
      translator_agent_id: agent.id,
      translator_agent_name: agent.name,
      translator_agent_icon: agent.icon_url || ''
    });
    toast.info(`Translator agent "${agent.name}" selected — click Save to confirm`);
  };

  const handleSaveHeroMedia = async () => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API_URL}/api/admin/hero-media`, heroMedia, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Hero media saved successfully');
    } catch (error) {
      toast.error('Failed to save hero media');
    }
  };

  const handleSaveSendGridConfig = async () => {
    const currentToken = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API_URL}/api/admin/sendgrid-config`, sendgridConfig, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      toast.success('Configuration SendGrid enregistrée');
    } catch (error) {
      toast.error('Impossible d’enregistrer la configuration SendGrid');
      console.error(error);
    }
  };

  const handleUpdateMessageStatus = async (messageId, newStatus) => {
    const currentToken = (localStorage.getItem('admin_token') || '').trim();
    if (!currentToken) {
      toast.error('Session inactive — reconnectez-vous.');
      return;
    }
    try {
      await axios.patch(
        `${API_URL}/api/admin/contact-messages/${encodeURIComponent(messageId)}?status=${encodeURIComponent(newStatus)}`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } },
      );
      await loadData();
    } catch (error) {
      console.error('Error updating message status:', error);
      alert('Failed to update message status');
    }
  };

  const handleDeleteContactMessage = async (messageId) => {
    const currentToken = (localStorage.getItem('admin_token') || '').trim();
    if (!currentToken) {
      toast.error('Session inactive — reconnectez-vous.');
      setContactMessageDeleteId(null);
      return;
    }
    try {
      // POST évite bien des 401 causés par des proxies/CDN sur DELETE (+ header Authorization).
      await axios.post(
        `${API_URL}/api/admin/contact-messages/${encodeURIComponent(messageId)}/delete`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } },
      );
      setContactMessageDeleteId(null);
      toast.success('Message supprimé');
      await loadData();
    } catch (error) {
      console.error('Error deleting contact message:', error);
      if (error.response?.status === 401) {
        toast.error('Session expirée ou non autorisé — reconnectez-vous.');
        localStorage.removeItem('admin_token');
        setToken(null);
        setIsAuthenticated(false);
        return;
      }
      toast.error('Impossible de supprimer le message');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-[#1A2332] border-[#2D3748]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-white">Admin Login</CardTitle>
            <CardDescription className="text-gray-400">Enter your credentials to access the admin panel</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-300">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-[#0F1729] border-[#2D3748] text-white placeholder:text-gray-500"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[#0F1729] border-[#2D3748] text-white placeholder:text-gray-500"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#0B1120]">
      <SeoHead title="Wownautic Admin" noindex />
      <header className="sticky top-0 z-20 shrink-0 border-b border-[#2D3748] bg-[#1A2332] px-6 py-4">
        <div className="flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 group" aria-label="Aller a l'accueil du site">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">W</span>
            </div>
            <h1 className="text-xl font-bold text-white group-hover:text-blue-300 transition-colors">Wownautic Admin</h1>
          </a>
          <Button 
            variant="outline" 
            onClick={handleLogout} 
            className="border-[#2D3748] text-gray-300 hover:bg-[#2D3748] hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <Tabs defaultValue="stats" className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1">
          <AdminSidebar />
          <main className="min-w-0 flex-1 overflow-y-auto p-6 lg:p-8">
          <TabsContent value="stats" className="mt-0 focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-[#1A2332] border-[#2D3748]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Newsletter Subscribers</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-white">{stats?.newsletter?.total || 0}</p>
                  <p className="text-sm text-gray-400 mt-1">{stats?.newsletter?.verified || 0} verified</p>
                </CardContent>
              </Card>
              <Card className="bg-[#1A2332] border-[#2D3748]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Pending Verification</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-yellow-500">{stats?.newsletter?.pending || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-[#1A2332] border-[#2D3748]">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Contact Messages</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-4xl font-bold text-blue-500">{contactMessages?.length || 0}</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="newsletter" className="mt-0 focus-visible:outline-none">
            <Card className="bg-[#1A2332] border-[#2D3748]">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="text-white">Newsletter Subscribers</CardTitle>
                    <CardDescription className="text-gray-400">Manage newsletter email subscriptions</CardDescription>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div className="bg-green-900/30 border border-green-700/50 rounded-lg px-4 py-2">
                      <span className="text-green-400 font-bold">{stats?.newsletter?.verified || 0}</span>
                      <span className="text-gray-400 ml-1">verified</span>
                    </div>
                    <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg px-4 py-2">
                      <span className="text-yellow-400 font-bold">{stats?.newsletter?.pending || 0}</span>
                      <span className="text-gray-400 ml-1">pending</span>
                    </div>
                    <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg px-4 py-2">
                      <span className="text-blue-400 font-bold">{stats?.newsletter?.total || 0}</span>
                      <span className="text-gray-400 ml-1">total</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#2D3748]">
                        <th className="text-left p-3 text-gray-400 font-medium">Email</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Status</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Subscribed</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Verified</th>
                        <th className="text-left p-3 text-gray-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newsletterSubscribers.map((sub) => (
                        <tr key={sub.id} className="border-b border-[#2D3748] hover:bg-[#2D3748]/50 transition-colors">
                          <td className="p-3 text-white font-medium">{sub.email}</td>
                          <td className="p-3">
                            {sub.verified ? (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-green-900/50 text-green-300 border border-green-700">
                                Verified
                              </span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-900/50 text-yellow-300 border border-yellow-700">
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-gray-300">
                            {sub.created_at ? new Date(sub.created_at).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </td>
                          <td className="p-3 text-gray-300">
                            {sub.verified_at ? new Date(sub.verified_at).toLocaleDateString('fr-FR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </td>
                          <td className="p-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm(`Delete ${sub.email}?`)) {
                                  try {
                                    const t = (localStorage.getItem('admin_token') || '').trim();
                                    if (!t) {
                                      toast.error('Session inactive — reconnectez-vous.');
                                      return;
                                    }
                                    await axios.delete(`${API_URL}/api/admin/newsletter/${encodeURIComponent(sub.id)}`, {
                                      headers: { Authorization: `Bearer ${t}` },
                                    });
                                    toast.success('Subscriber deleted');
                                    loadData();
                                  } catch (err) {
                                    toast.error('Failed to delete subscriber');
                                  }
                                }
                              }}
                              className="bg-red-900/50 text-red-300 border-red-700 hover:bg-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {newsletterSubscribers.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No subscribers yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="offers" className="mt-0 focus-visible:outline-none">
            <Card className="bg-[#1A2332] border-[#2D3748]">
              <CardHeader>
                <CardTitle className="text-white">Manage Website Offers</CardTitle>
                <CardDescription className="text-gray-400">
                  Gérez vos offres, catégories et slugs depuis cet espace unifié
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="offers-list" className="space-y-4">
                  <TabsList className="flex h-auto min-h-9 w-full flex-wrap items-center justify-start gap-1 bg-[#0F1729] border border-[#2D3748]">
                    <TabsTrigger value="offers-list" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400">
                      Offers
                    </TabsTrigger>
                    <TabsTrigger value="categories" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400">
                      Categories
                    </TabsTrigger>
                    <TabsTrigger value="slug" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-gray-400">
                      Slug
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="offers-list" className="space-y-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 bg-[#0B1120] border border-[#2D3748] rounded-lg">
                      <div className="text-sm text-gray-300 space-y-1">
                        <p>
                          <span className="text-gray-500">API active :</span>{' '}
                          <strong className="text-white">
                            {wowbookingSource?.mode === 'dev'
                              ? 'Développement'
                              : wowbookingSource?.mode === 'preprod'
                                ? 'Pré-production'
                                : wowbookingSource?.mode === 'production'
                                  ? 'Production'
                                  : '—'}
                          </strong>
                        </p>
                        {wowbookingSource?.tenant_slug ? (
                          <p className="text-xs text-gray-500 font-mono truncate">
                            {wowbookingSource.api_url} · tenant {wowbookingSource.tenant_slug}
                            {wowbookingSource.count != null ? ` · ${wowbookingSource.count} offre(s)` : ''}
                          </p>
                        ) : null}
                        {wowbookingOffersError ? (
                          <p className="text-xs text-red-400">{wowbookingOffersError}</p>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={offersReloading}
                        onClick={() => loadWowbookingOffers({ showToast: true })}
                        className="border-[#2D3748] text-white hover:bg-[#2D3748] shrink-0"
                      >
                        {offersReloading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Recharger les offres
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {allOffers.length === 0 ? (
                        <p className="text-gray-400 text-center py-8">
                          {wowbookingOffersError
                            ? 'Aucune offre (erreur API — vérifiez token et tenant dans WowBooking)'
                            : 'Aucune offre sur cet environnement API'}
                        </p>
                      ) : (
                        allOffers.map((offer) => (
                          <div
                            key={offer.id}
                            className="flex items-center justify-between p-4 bg-[#0F1729] border border-[#2D3748] rounded-lg hover:border-blue-500 transition-colors"
                          >
                            <div className="flex items-center gap-4 flex-1">
                              {offer.featured_image_url && (
                                <img
                                  src={offer.featured_image_url}
                                  alt={offer.title}
                                  className="w-16 h-16 object-cover rounded"
                                />
                              )}
                              <div className="flex-1">
                                <h3 className="text-white font-semibold">{offer.title}</h3>
                                <p className="text-gray-400 text-sm">
                                  {offer.duration_minutes} min • {offer.promo_price_ht}€ • {offer.offer_type}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openTranslationModal(offer)}
                                className="border-[#2D3748] text-gray-300 hover:bg-[#2D3748] hover:text-white"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={visibleOffers.includes(offer.id)}
                                  onChange={() => toggleOfferVisibility(offer.id)}
                                  className="w-5 h-5 rounded border-[#2D3748] bg-[#0F1729] checked:bg-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-gray-300 text-sm">
                                  {visibleOffers.includes(offer.id) ? 'Visible' : 'Hidden'}
                                </span>
                              </label>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                      <p className="text-blue-300 text-sm">
                        <strong>{visibleOffers.length}</strong> sur <strong>{allOffers.length}</strong> offres visibles sur le site
                        {wowbookingSource?.mode ? (
                          <span className="text-blue-400/80"> (environnement {wowbookingSource.mode})</span>
                        ) : null}
                      </p>
                      <p className="text-blue-300/80 text-xs mt-1">
                        Les cases à cocher s&apos;appliquent à l&apos;environnement API actif. Après changement d&apos;API, recliquez « Recharger les offres » puis cochez les offres à afficher.
                      </p>
                      <p className="text-blue-300 text-sm mt-2">
                        <strong>{visibleBundles.length}</strong> bundle(s) visible — gérer dans l’onglet <strong>Bundles</strong> (barre du haut)
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="categories" className="space-y-4">
                    <div className="bg-[#0B1120] border border-[#2D3748] rounded-lg p-4">
                      <h3 className="text-white font-semibold mb-4">Créer une catégorie</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-gray-300">Nom principal</Label>
                          <Input
                            placeholder="Nom (ex: Jet-Ski)"
                            value={newCategory.name}
                            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                            className="bg-[#1A2332] border-[#2D3748] text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Slug</Label>
                          <Input
                            placeholder="Slug (ex: jetskis)"
                            value={newCategory.slug}
                            onChange={(e) => setNewCategory({ ...newCategory, slug: e.target.value })}
                            className="bg-[#1A2332] border-[#2D3748] text-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <Label className="text-gray-300">Nom FR</Label>
                          <Input
                            placeholder="Jet-Ski"
                            value={newCategory.names?.fr || ''}
                            onChange={(e) => setNewCategory({ ...newCategory, names: { ...newCategory.names, fr: e.target.value } })}
                            className="bg-[#1A2332] border-[#2D3748] text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Name EN</Label>
                          <Input
                            placeholder="Jet Ski"
                            value={newCategory.names?.en || ''}
                            onChange={(e) => setNewCategory({ ...newCategory, names: { ...newCategory.names, en: e.target.value } })}
                            className="bg-[#1A2332] border-[#2D3748] text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Nombre ES</Label>
                          <Input
                            placeholder="Moto de agua"
                            value={newCategory.names?.es || ''}
                            onChange={(e) => setNewCategory({ ...newCategory, names: { ...newCategory.names, es: e.target.value } })}
                            className="bg-[#1A2332] border-[#2D3748] text-white"
                          />
                        </div>
                      </div>
                      <Button onClick={handleCreateCategory} className="bg-blue-600 hover:bg-blue-700">
                        Ajouter catégorie
                      </Button>
                    </div>

                    <div className="bg-[#0B1120] border border-[#2D3748] rounded-lg p-4">
                      <h3 className="text-white font-semibold mb-4">Catégories existantes</h3>
                      <div className="space-y-3">
                        {categoriesCatalog.length === 0 ? (
                          <p className="text-gray-400 text-sm">Aucune catégorie pour l'instant.</p>
                        ) : (
                          categoriesCatalog.map((cat) => {
                            const catNames = cat.names || {};
                            return (
                              <div key={cat.slug} className="bg-[#1A2332] border border-[#2D3748] rounded-lg p-4">
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <p className="text-white font-medium text-lg">{cat.name || cat.slug}</p>
                                    <p className="text-xs text-gray-400 font-mono">{cat.slug}</p>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteCategory(cat.slug)}
                                    className="bg-red-900/50 text-red-300 border-red-700 hover:bg-red-800"
                                  >
                                    Supprimer
                                  </Button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div>
                                    <Label className="text-gray-400 text-xs">FR</Label>
                                    <Input
                                      defaultValue={catNames.fr || cat.name || ''}
                                      onBlur={(e) => {
                                        const val = e.target.value.trim();
                                        if (val !== (catNames.fr || cat.name || '')) {
                                          handleUpdateCategoryNames(cat.slug, { ...catNames, fr: val });
                                        }
                                      }}
                                      className="bg-[#0F1729] border-[#2D3748] text-white text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">EN</Label>
                                    <Input
                                      defaultValue={catNames.en || ''}
                                      placeholder="English name"
                                      onBlur={(e) => {
                                        const val = e.target.value.trim();
                                        if (val !== (catNames.en || '')) {
                                          handleUpdateCategoryNames(cat.slug, { ...catNames, en: val });
                                        }
                                      }}
                                      className="bg-[#0F1729] border-[#2D3748] text-white text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-400 text-xs">ES</Label>
                                    <Input
                                      defaultValue={catNames.es || ''}
                                      placeholder="Nombre en español"
                                      onBlur={(e) => {
                                        const val = e.target.value.trim();
                                        if (val !== (catNames.es || '')) {
                                          handleUpdateCategoryNames(cat.slug, { ...catNames, es: val });
                                        }
                                      }}
                                      className="bg-[#0F1729] border-[#2D3748] text-white text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="bg-[#0B1120] border border-[#2D3748] rounded-lg p-4">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <h3 className="text-white font-semibold">Assigner les catégories aux offres</h3>
                        <Input
                          placeholder="Rechercher une offre..."
                          value={offerCategorySearch}
                          onChange={(e) => setOfferCategorySearch(e.target.value)}
                          className="max-w-xs bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>
                      <div className="space-y-3">
                        {allOffers
                          .filter((offer) => {
                            const q = offerCategorySearch.trim().toLowerCase();
                            if (!q) return true;
                            return (offer.title || '').toLowerCase().includes(q) || String(offer.id).includes(q);
                          })
                          .map((offer) => {
                            const draft = offerCategoryDrafts[offer.id] || { categories: [], primary_category: '' };
                            return (
                              <div key={`cat-${offer.id}`} className="p-4 bg-[#1A2332] border border-[#2D3748] rounded-lg">
                                <div className="flex justify-between items-center mb-3">
                                  <div>
                                    <p className="text-white font-medium">{offer.title}</p>
                                    <p className="text-xs text-gray-400">ID #{offer.id}</p>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleSaveOfferCategories(offer)}
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                  >
                                    Enregistrer catégories
                                  </Button>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                                  {categoriesCatalog.map((cat) => (
                                    <label key={`${offer.id}-${cat.slug}`} className="flex items-center gap-2 text-sm text-gray-300 bg-[#0F1729] border border-[#2D3748] rounded px-2 py-1">
                                      <input
                                        type="checkbox"
                                        checked={(draft.categories || []).includes(cat.slug)}
                                        onChange={() => toggleOfferCategory(offer.id, cat.slug)}
                                      />
                                      <span>{cat.name || cat.slug}</span>
                                    </label>
                                  ))}
                                </div>
                                <div>
                                  <Label className="text-gray-300">Catégorie principale (slug)</Label>
                                  <select
                                    value={draft.primary_category || ''}
                                    onChange={(e) => setOfferCategoryDrafts((prev) => ({
                                      ...prev,
                                      [offer.id]: { ...draft, primary_category: e.target.value }
                                    }))}
                                    className="w-full mt-1 px-3 py-2 bg-[#0F1729] border border-[#2D3748] rounded-md text-white"
                                  >
                                    <option value="">Sélectionner</option>
                                    {(draft.categories || []).map((slug) => {
                                      const cat = categoriesCatalog.find((c) => c.slug === slug);
                                      return <option key={slug} value={slug}>{cat?.name || slug}</option>;
                                    })}
                                  </select>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="slug" className="space-y-4">
                    <div className="bg-[#0B1120] border border-[#2D3748] rounded-lg p-4">
                      <h3 className="text-white font-semibold mb-4">Créer un nouveau slug</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <Label className="text-gray-300">Offer ID *</Label>
                          <Input
                            type="number"
                            placeholder="5"
                            value={newSlug.offer_id}
                            onChange={(e) => setNewSlug({ ...newSlug, offer_id: e.target.value })}
                            className="bg-[#1A2332] border-[#2D3748] text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Slug *</Label>
                          <Input
                            placeholder="doqueve-majestic-4h"
                            value={newSlug.slug}
                            onChange={(e) => setNewSlug({ ...newSlug, slug: e.target.value })}
                            className="bg-[#1A2332] border-[#2D3748] text-white"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300">Catégorie principale</Label>
                          <select
                            value={newSlug.primary_category}
                            onChange={(e) => setNewSlug({ ...newSlug, primary_category: e.target.value })}
                            className="w-full px-3 py-2 bg-[#1A2332] border border-[#2D3748] rounded-md text-white"
                          >
                            <option value="">Sélectionner</option>
                            {categoriesCatalog.map((cat) => (
                              <option key={`new-${cat.slug}`} value={cat.slug}>{cat.name || cat.slug}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <Button onClick={handleCreateSlug} className="bg-blue-600 hover:bg-blue-700">
                        Créer le slug
                      </Button>
                    </div>

                    <div className="bg-[#0B1120] border border-[#2D3748] rounded-lg p-4">
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <h3 className="text-white font-semibold">Slugs existants ({offerSlugs.length})</h3>
                        <Input
                          placeholder="Rechercher un slug..."
                          value={offerSlugSearch}
                          onChange={(e) => setOfferSlugSearch(e.target.value)}
                          className="max-w-xs bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-[#2D3748]">
                              <th className="text-left p-3 text-gray-400 font-medium">Offer ID</th>
                              <th className="text-left p-3 text-gray-400 font-medium">Slug</th>
                              <th className="text-left p-3 text-gray-400 font-medium">Catégorie principale</th>
                              <th className="text-left p-3 text-gray-400 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {offerSlugs
                              .filter((slugData) => {
                                const q = offerSlugSearch.trim().toLowerCase();
                                if (!q) return true;
                                return (slugData.slug || '').toLowerCase().includes(q) || String(slugData.offer_id).includes(q);
                              })
                              .map((slugData) => {
                                const offer = allOffers.find((o) => o.id === slugData.offer_id);
                                const draft = offerSlugDrafts[slugData.offer_id] || { slug: slugData.slug || '', primary_category: slugData.primary_category || slugData.category || '' };
                                const categoryDraft = offerCategoryDrafts[slugData.offer_id] || { categories: slugData.categories || (slugData.category ? [slugData.category] : []) };
                                const categoryOptions = (categoryDraft.categories || []).length
                                  ? categoryDraft.categories
                                  : categoriesCatalog.map((c) => c.slug);

                                return (
                                  <tr key={slugData.offer_id} className="border-b border-[#2D3748] hover:bg-[#2D3748]/50">
                                    <td className="p-3 text-white font-medium">
                                      {slugData.offer_id}
                                      {offer && <div className="text-xs text-gray-400 mt-1">{offer.title}</div>}
                                    </td>
                                    <td className="p-3">
                                      <Input
                                        value={draft.slug}
                                        onChange={(e) => setOfferSlugDrafts((prev) => ({
                                          ...prev,
                                          [slugData.offer_id]: { ...draft, slug: e.target.value }
                                        }))}
                                        className="bg-[#0B1120] border-[#2D3748] text-white text-sm"
                                      />
                                    </td>
                                    <td className="p-3">
                                      <select
                                        value={draft.primary_category || ''}
                                        onChange={(e) => setOfferSlugDrafts((prev) => ({
                                          ...prev,
                                          [slugData.offer_id]: { ...draft, primary_category: e.target.value }
                                        }))}
                                        className="w-full px-3 py-2 bg-[#0B1120] border border-[#2D3748] rounded-md text-white text-sm"
                                      >
                                        <option value="">Sélectionner</option>
                                        {categoryOptions.map((slug) => {
                                          const cat = categoriesCatalog.find((c) => c.slug === slug);
                                          return <option key={`${slugData.offer_id}-${slug}`} value={slug}>{cat?.name || slug}</option>;
                                        })}
                                      </select>
                                    </td>
                                    <td className="p-3">
                                      <div className="flex gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleUpdateSlug(slugData.offer_id, {
                                            slug: draft.slug,
                                            primary_category: draft.primary_category
                                          })}
                                          className="bg-blue-900/50 text-blue-300 border-blue-700 hover:bg-blue-800"
                                        >
                                          Enregistrer
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleDeleteSlug(slugData.offer_id)}
                                          className="bg-red-900/50 text-red-300 border-red-700 hover:bg-red-800"
                                        >
                                          Supprimer
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bundles" className="mt-0 focus-visible:outline-none">
            <Card className="bg-[#1A2332] border-[#2D3748]">
              <CardHeader>
                <CardTitle className="text-white">Offres chapeau (bundles)</CardTitle>
                <CardDescription className="text-gray-400">
                  Regroupe plusieurs offres WowBooking sous un seul slug ; visibilité indépendante des offres simples.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-[#0B1120] border border-[#2D3748] rounded-lg p-4 space-y-4">
                  <h3 className="text-white font-semibold">Create umbrella offer (bundle)</h3>
                  <p className="text-gray-400 text-sm">
                    Slug unique (non utilisé par une autre offre). Choisissez les offres WowBooking ci-dessous ; l’ordre dans la colonne de droite définit l’ordre d’affichage des variantes.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-gray-300">Slug (URL)</Label>
                      <Input
                        className="bg-[#1A2332] border-[#2D3748] text-white"
                        value={bundleForm.slug}
                        onChange={(e) => setBundleForm({ ...bundleForm, slug: e.target.value })}
                        placeholder="e.g. doqueve-location-flex"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Primary category slug</Label>
                      <Input
                        className="bg-[#1A2332] border-[#2D3748] text-white"
                        value={bundleForm.primary_category}
                        onChange={(e) => setBundleForm({ ...bundleForm, primary_category: e.target.value })}
                        placeholder="boat-tours"
                      />
                    </div>
                    <div className="md:col-span-2 grid gap-4 lg:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-gray-300">Offres du bundle</Label>
                        <Input
                          className="bg-[#1A2332] border-[#2D3748] text-white"
                          value={bundleOfferSearch}
                          onChange={(e) => setBundleOfferSearch(e.target.value)}
                          placeholder="Filtrer par titre ou par n° d’offre…"
                        />
                        <div className="max-h-72 overflow-y-auto rounded-lg border border-[#2D3748] bg-[#0F1729] divide-y divide-[#2D3748]">
                          {filteredOffersForBundle.length === 0 ? (
                            <p className="p-4 text-gray-500 text-sm">
                              {allOffers.length === 0
                                ? 'Aucune offre chargée (vérifiez WowBooking / connexion admin).'
                                : 'Aucune offre ne correspond au filtre.'}
                            </p>
                          ) : (
                            filteredOffersForBundle
                              .map((offer) => ({ offer, oid: wbOfferId(offer) }))
                              .filter(({ oid }) => oid != null)
                              .map(({ offer, oid }) => {
                                const checked = bundleVariantOrder.includes(oid);
                                return (
                                  <label
                                    key={oid}
                                    className="flex cursor-pointer items-center gap-3 p-3 hover:bg-[#1A2332]/80"
                                  >
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 shrink-0 rounded border-[#2D3748]"
                                      checked={checked}
                                      onChange={() => toggleBundleVariant(oid)}
                                    />
                                    {offer.featured_image_url ? (
                                      <img
                                        src={offer.featured_image_url}
                                        alt=""
                                        className="h-11 w-11 shrink-0 rounded object-cover"
                                      />
                                    ) : (
                                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded bg-[#1A2332] text-xs text-gray-500">
                                        —
                                      </div>
                                    )}
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-medium text-white">{offer.title}</p>
                                      <p className="text-xs text-gray-500">ID WowBooking · {oid}</p>
                                    </div>
                                  </label>
                                );
                              })
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-300">Ordre des variantes (haut → bas)</Label>
                        <div className="min-h-[12rem] max-h-72 overflow-y-auto rounded-lg border border-[#2D3748] bg-[#0F1729] p-2">
                          {bundleVariantOrder.length === 0 ? (
                            <p className="p-3 text-sm text-gray-500">Cochez des offres dans la liste de gauche.</p>
                          ) : (
                            <ul className="space-y-1">
                              {bundleVariantOrder.map((id, idx) => {
                                const o = allOffers.find((x) => wbOfferId(x) === id);
                                return (
                                  <li
                                    key={id}
                                    className="flex items-center gap-1 rounded-md border border-[#2D3748] bg-[#0B1120] px-2 py-1.5"
                                  >
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-8 w-8 shrink-0 border-[#2D3748] p-0 text-gray-300"
                                      disabled={idx === 0}
                                      onClick={() => moveBundleVariant(idx, -1)}
                                      aria-label="Monter"
                                    >
                                      <ChevronUp className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="h-8 w-8 shrink-0 border-[#2D3748] p-0 text-gray-300"
                                      disabled={idx === bundleVariantOrder.length - 1}
                                      onClick={() => moveBundleVariant(idx, 1)}
                                      aria-label="Descendre"
                                    >
                                      <ChevronDown className="h-4 w-4" />
                                    </Button>
                                    <span className="min-w-0 flex-1 truncate text-sm text-white">
                                      {o?.title || `Offre #${id}`}
                                    </span>
                                    <span className="shrink-0 text-xs text-gray-500">#{id}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-gray-300">Display title (optional)</Label>
                      <Input
                        className="bg-[#1A2332] border-[#2D3748] text-white"
                        value={bundleForm.display_title}
                        onChange={(e) => setBundleForm({ ...bundleForm, display_title: e.target.value })}
                        placeholder="Override title on tile & detail"
                      />
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <Label className="text-gray-300">Image mise en avant</Label>
                      <div
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            bundleFileInputRef.current?.click();
                          }
                        }}
                        onDragEnter={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setBundleImageDragActive(true);
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setBundleImageDragActive(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (e.currentTarget.contains(e.relatedTarget)) return;
                          setBundleImageDragActive(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setBundleImageDragActive(false);
                          const f = e.dataTransfer.files?.[0];
                          if (f) uploadBundleFeaturedImage(f);
                        }}
                        onClick={() => !bundleImageUploading && bundleFileInputRef.current?.click()}
                        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 transition-colors ${
                          bundleImageDragActive
                            ? 'border-blue-500 bg-blue-900/25'
                            : 'border-[#2D3748] bg-[#0F1729] hover:border-blue-600/50'
                        } ${bundleImageUploading ? 'pointer-events-none opacity-60' : ''}`}
                      >
                        <input
                          ref={bundleFileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadBundleFeaturedImage(f);
                            e.target.value = '';
                          }}
                        />
                        {bundleImageUploading ? (
                          <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                        ) : (
                          <Upload className="h-8 w-8 text-gray-400" />
                        )}
                        <p className="text-center text-sm text-gray-400">
                          Glissez-déposez une image, ou cliquez pour choisir un fichier
                        </p>
                        <p className="text-center text-xs text-gray-600">JPEG, PNG ou WebP · max 5 Mo</p>
                      </div>
                      {bundleForm.featured_image_url ? (
                        <div className="flex flex-wrap items-start gap-3">
                          <img
                            src={bundleForm.featured_image_url}
                            alt="Aperçu"
                            className="max-h-36 rounded-lg border border-[#2D3748] object-contain"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-[#2D3748] text-gray-300"
                            onClick={() => setBundleForm((f) => ({ ...f, featured_image_url: '' }))}
                          >
                            Retirer l’image
                          </Button>
                        </div>
                      ) : null}
                      <div className="pt-1">
                        <Label className="text-gray-500 text-xs">Ou coller une URL</Label>
                        <Input
                          className="mt-1 bg-[#1A2332] border-[#2D3748] text-white"
                          value={bundleForm.featured_image_url}
                          onChange={(e) => setBundleForm({ ...bundleForm, featured_image_url: e.target.value })}
                          placeholder="https://… ou /images/…"
                        />
                      </div>
                    </div>
                    <label className="flex items-center gap-2 text-gray-300 text-sm md:col-span-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bundleForm.hide_children_on_homepage}
                        onChange={(e) =>
                          setBundleForm({ ...bundleForm, hide_children_on_homepage: e.target.checked })
                        }
                        className="w-4 h-4"
                      />
                      Hide child offers from homepage when bundle is visible (recommended)
                    </label>
                  </div>
                  <Button onClick={createOfferBundle} className="bg-blue-600 hover:bg-blue-700 text-white">
                    Create bundle
                  </Button>
                </div>

                <div className="space-y-3">
                  <h3 className="text-white font-semibold">Existing bundles</h3>
                  {bundles.length === 0 ? (
                    <p className="text-gray-500 text-sm">No bundles yet</p>
                  ) : (
                    bundles.map((b) => (
                      <div
                        key={b.slug}
                        className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-[#0F1729] border border-[#2D3748] rounded-lg"
                      >
                        <div>
                          <p className="text-white font-semibold">{b.slug}</p>
                          <p className="text-gray-400 text-sm">
                            Catégorie : {b.primary_category} ·{' '}
                            {(b.variant_offer_ids || [])
                              .map((vid) => {
                                const vn = Number(vid);
                                const o = allOffers.find((x) => wbOfferId(x) === vn);
                                return o ? `${o.title} (#${vn})` : `#${vn}`;
                              })
                              .join(' → ')}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="flex items-center gap-2 cursor-pointer text-gray-300 text-sm">
                            <input
                              type="checkbox"
                              checked={visibleBundles.includes(b.slug)}
                              onChange={() => toggleBundleVisibility(b.slug)}
                              className="w-5 h-5 rounded border-[#2D3748]"
                            />
                            {visibleBundles.includes(b.slug) ? 'Visible on site' : 'Hidden'}
                          </label>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-700 text-red-300 hover:bg-red-900/40"
                            onClick={() => deleteOfferBundle(b.slug)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="config" className="mt-0 focus-visible:outline-none">
            <div className="space-y-6">
              <Card className="bg-[#1A2332] border-[#2D3748]">
                <CardHeader>
                  <CardTitle className="text-white">Add/Update Configuration</CardTitle>
                  <CardDescription className="text-gray-400">Manage API keys and application settings</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-300">Key</Label>
                      <Input
                        placeholder="e.g., WOWBOOKING_API_TOKEN"
                        value={newConfigKey}
                        onChange={(e) => setNewConfigKey(e.target.value)}
                        className="bg-[#0F1729] border-[#2D3748] text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Value</Label>
                      <Input
                        placeholder="Configuration value"
                        value={newConfigValue}
                        onChange={(e) => setNewConfigValue(e.target.value)}
                        className="bg-[#0F1729] border-[#2D3748] text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Description (optional)</Label>
                      <Input
                        placeholder="What is this for?"
                        value={newConfigDesc}
                        onChange={(e) => setNewConfigDesc(e.target.value)}
                        className="bg-[#0F1729] border-[#2D3748] text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>
                  <Button onClick={addOrUpdateConfig} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
                    Save Configuration
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-[#1A2332] border-[#2D3748]">
                <CardHeader>
                  <CardTitle className="text-white">Current Configurations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {configs.map((config) => (
                      <div key={config.key} className="flex items-center justify-between p-4 border border-[#2D3748] rounded-lg bg-[#0F1729] hover:bg-[#1A2332]/50 transition-colors">
                        <div className="flex-1">
                          <p className="font-semibold text-white">{config.key}</p>
                          <p className="text-sm text-gray-400 font-mono">{config.value}</p>
                          {config.description && (
                            <p className="text-xs text-gray-500 mt-1">{config.description}</p>
                          )}
                        </div>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteConfig(config.key)}
                          className="bg-red-900/50 hover:bg-red-900 border border-red-700 text-red-300"
                        >
                          Delete
                        </Button>
                      </div>
                    ))}
                    {configs.length === 0 && (
                      <p className="text-center text-gray-500 py-8">No configurations yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="wowbooking" className="mt-0 space-y-4 focus-visible:outline-none">
            <AdminWowBookingTab
              config={wowbookingConfig}
              setConfig={setWowbookingConfig}
              onSave={handleSaveWowbookingConfig}
              onVerify={handleVerifyWowbookingConnection}
              verifyLoading={wowbookingVerifyLoading}
              verifyResult={wowbookingVerifyResult}
              isDevBackoffice={isDevBackoffice}
            />
          </TabsContent>

          <TabsContent value="stripe" className="mt-0 space-y-4 focus-visible:outline-none">
                <Card className="bg-[#1A2332] border-[#2D3748]">
                  <CardHeader>
                    <CardTitle className="text-white">Stripe Payment Configuration</CardTitle>
                    <CardDescription className="text-gray-400">
                      Configure your Stripe API keys for payment processing
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Mode Selection */}
                    <div className="space-y-2">
                      <Label className="text-white">Mode</Label>
                      <select
                        value={stripeConfig.mode}
                        onChange={(e) => setStripeConfig({ ...stripeConfig, mode: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0B1120] border border-[#2D3748] rounded-md text-white"
                      >
                        <option value="test">Test Mode</option>
                        <option value="live">Live Mode</option>
                      </select>
                      <p className="text-xs text-gray-400">
                        {stripeConfig.mode === 'test' ? '🧪 Test mode - Use test cards' : '🚀 Live mode - Real payments'}
                      </p>
                    </div>

                    {/* Test Keys */}
                    <div className="space-y-4 p-4 bg-[#0B1120] rounded-lg border border-[#2D3748]">
                      <h3 className="text-white font-semibold">Test Mode Keys</h3>
                      
                      <div className="space-y-2">
                        <Label className="text-white">Test Publishable Key</Label>
                        <Input
                          type="text"
                          placeholder="pk_test_..."
                          value={stripeConfig.test_publishable_key}
                          onChange={(e) => setStripeConfig({ ...stripeConfig, test_publishable_key: e.target.value })}
                          className="bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Test Secret Key</Label>
                        <Input
                          type="password"
                          placeholder="sk_test_..."
                          value={stripeConfig.test_secret_key}
                          onChange={(e) => setStripeConfig({ ...stripeConfig, test_secret_key: e.target.value })}
                          className="bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Test Webhook Secret</Label>
                        <Input
                          type="password"
                          placeholder="whsec_..."
                          value={stripeConfig.test_webhook_secret}
                          onChange={(e) => setStripeConfig({ ...stripeConfig, test_webhook_secret: e.target.value })}
                          className="bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>
                    </div>

                    {/* Live Keys */}
                    <div className="space-y-4 p-4 bg-[#0B1120] rounded-lg border border-orange-500/30">
                      <h3 className="text-white font-semibold flex items-center gap-2">
                        <span className="text-orange-500">⚠️</span> Live Mode Keys
                      </h3>
                      
                      <div className="space-y-2">
                        <Label className="text-white">Live Publishable Key</Label>
                        <Input
                          type="text"
                          placeholder="pk_live_..."
                          value={stripeConfig.live_publishable_key}
                          onChange={(e) => setStripeConfig({ ...stripeConfig, live_publishable_key: e.target.value })}
                          className="bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Live Secret Key</Label>
                        <Input
                          type="password"
                          placeholder="sk_live_..."
                          value={stripeConfig.live_secret_key}
                          onChange={(e) => setStripeConfig({ ...stripeConfig, live_secret_key: e.target.value })}
                          className="bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Live Webhook Secret</Label>
                        <Input
                          type="password"
                          placeholder="whsec_..."
                          value={stripeConfig.live_webhook_secret}
                          onChange={(e) => setStripeConfig({ ...stripeConfig, live_webhook_secret: e.target.value })}
                          className="bg-[#1A2332] border-[#2D3748] text-white"
                        />
                      </div>
                    </div>

                    {/* Help Text */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <h4 className="text-blue-400 font-semibold mb-2">📚 How to get your Stripe keys</h4>
                      <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                        <li>Go to <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">Stripe Dashboard</a></li>
                        <li>Navigate to Developers → API keys</li>
                        <li>Copy your Publishable and Secret keys</li>
                        <li>For webhooks: Developers → Webhooks → Add endpoint</li>
                        <li>
                          Webhook URL ({isDevBackoffice ? 'development environment' : 'production environment'}):{' '}
                          <code className="bg-[#0B1120] px-2 py-1 rounded">{stripeWebhookUrl}</code>
                        </li>
                        <li>Events: payment_intent.succeeded, payment_intent.payment_failed</li>
                      </ol>
                    </div>

                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 space-y-3">
                      <h4 className="text-emerald-400 font-semibold">🔌 Stripe connectivity check</h4>
                      <p className="text-sm text-gray-300">
                        This test checks API connectivity to Stripe for the selected mode and validates that the webhook endpoint exists on your Stripe account.
                      </p>
                      <Button
                        onClick={handleVerifyStripeConnection}
                        disabled={stripeVerifyLoading}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                      >
                        {stripeVerifyLoading ? 'Checking Stripe connection…' : 'Test Stripe Connection'}
                      </Button>
                      {stripeVerifyResult && (
                        <div className="rounded border border-[#2D3748] bg-[#0B1120] p-3 text-sm text-gray-200 space-y-1">
                          <p>
                            API status:{' '}
                            <span className={stripeVerifyResult.ok ? 'text-emerald-400' : 'text-red-400'}>
                              {stripeVerifyResult.ok ? 'Connected' : 'Failed'}
                            </span>
                          </p>
                          {stripeVerifyResult.mode && <p>Mode checked: <span className="text-white">{stripeVerifyResult.mode}</span></p>}
                          {stripeVerifyResult.account_id && <p>Stripe account: <span className="text-white">{stripeVerifyResult.account_id}</span></p>}
                          {stripeVerifyResult.webhook_url_checked && (
                            <p>
                              Webhook URL:{' '}
                              <code className="bg-[#111827] px-1.5 py-0.5 rounded">{stripeVerifyResult.webhook_url_checked}</code>
                            </p>
                          )}
                          {typeof stripeVerifyResult.webhook_endpoint_found === 'boolean' && (
                            <p>
                              Webhook endpoint in Stripe:{' '}
                              <span className={stripeVerifyResult.webhook_endpoint_found ? 'text-emerald-400' : 'text-orange-400'}>
                                {stripeVerifyResult.webhook_endpoint_found ? 'Found' : 'Not found'}
                              </span>
                            </p>
                          )}
                          {stripeVerifyResult.error && <p className="text-red-400">{stripeVerifyResult.error}</p>}
                        </div>
                      )}
                    </div>

                    <Button 
                      onClick={handleSaveStripeConfig}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      Save Stripe Configuration
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

          <TabsContent value="sendgrid" className="mt-0 space-y-4 focus-visible:outline-none">
            <Card className="bg-[#1A2332] border-[#2D3748]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="h-5 w-5 text-[#FF6B35]" />
                  SendGrid
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Envoi transactional (formulaire contact, newsletter…) via votre compte SendGrid.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <div className="space-y-5">
                  <p className="text-sm font-medium text-gray-300">Connexion · expéditeur</p>
                  <div className="space-y-2">
                    <Label htmlFor="sg-api-key" className="text-white">Clé API SendGrid</Label>
                    <Input
                      id="sg-api-key"
                      type="password"
                      autoComplete="off"
                      placeholder="SG.xxxxxxxxxxxxxxxxxxxxx"
                      value={sendgridConfig.api_key}
                      onChange={(e) => setSendgridConfig({ ...sendgridConfig, api_key: e.target.value })}
                      className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                    />
                    <p className="text-xs text-gray-400">
                      Obtenez une clé sur{' '}
                      <a
                        href="https://app.sendgrid.com/settings/api_keys"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline"
                      >
                        le tableau SendGrid (API Keys)
                      </a>
                      .
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sg-from" className="text-white">Adresse « De »</Label>
                    <Input
                      id="sg-from"
                      type="email"
                      placeholder="noreply@votredomaine.com"
                      value={sendgridConfig.from_email}
                      onChange={(e) => setSendgridConfig({ ...sendgridConfig, from_email: e.target.value })}
                      className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                    />
                    <p className="text-xs text-gray-400">
                      Doit être une adresse ou un domaine vérifiés dans SendGrid (Sender Authentication).
                    </p>
                  </div>
                </div>

                <div className="border-t border-[#2D3748] pt-6 space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-300">Formulaire contact · notifications</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Destinées au back-office : chaque message visiteur est envoyé à ces boîtes (même objet,
                      même contenu · Reply-To = visiteur).
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sg-to-primary" className="text-white">
                      Premier destinataire <span className="text-orange-400">*</span>
                    </Label>
                    <Input
                      id="sg-to-primary"
                      type="text"
                      inputMode="email"
                      placeholder="commercial@entreprise.com"
                      value={sendgridConfig.to_email}
                      onChange={(e) => setSendgridConfig({ ...sendgridConfig, to_email: e.target.value })}
                      className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                    />
                    <p className="text-xs text-gray-400">
                      Obligatoire. Possibilité d’ajouter plusieurs adresses sur la même ligne, séparées par des{' '}
                      <strong className="text-gray-300">virgules</strong>.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sg-contact-cc" className="text-white">
                      Autre destinataire <span className="text-gray-500 font-normal text-xs">(optionnel)</span>
                    </Label>
                    <Input
                      id="sg-contact-cc"
                      type="text"
                      inputMode="email"
                      placeholder="chef@entreprise.com"
                      value={sendgridConfig.contact_notification_cc}
                      onChange={(e) =>
                        setSendgridConfig({ ...sendgridConfig, contact_notification_cc: e.target.value })
                      }
                      className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                    />
                    <p className="text-xs text-gray-400">
                      Reçoit une copie identique des notifications « formulaire contact ». Évitez les doublons :
                      même adresse qu’au-dessus = une seule copie envoyée.
                    </p>
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    type="button"
                    onClick={handleSaveSendGridConfig}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Enregistrer la configuration SendGrid
                  </Button>
                </div>

                <div className="p-4 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-lg">
                  <p className="text-sm text-orange-100/90 leading-relaxed">
                    <strong className="text-orange-300">À vérifier :</strong> compte SendGrid actif,
                    signature d’email validée ; sinon les envois échoueront même si cette page est bien
                    renseignée.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="messages" className="mt-0 space-y-4 focus-visible:outline-none">
            <Card className="bg-[#1A2332] border-[#2D3748]">
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4 text-white">Messages de Contact</h2>
                
                <div className="space-y-4">
                  {contactMessages.length === 0 ? (
                    <p className="text-gray-400">Aucun message reçu</p>
                  ) : (
                    contactMessages.map((msg) => (
                      <div key={msg.id} className="bg-[#0B1120] border border-[#2D3748] rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h3 className="text-white font-semibold">
                              {msg.firstName} {msg.lastName}
                            </h3>
                            <p className="text-sm text-gray-400">{msg.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${
                              msg.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                              msg.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-green-500/20 text-green-400'
                            }`}>
                              {msg.status === 'new' ? 'Nouveau' : 
                               msg.status === 'in_progress' ? 'En cours' : 'Traité'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {new Date(msg.created_at).toLocaleDateString('fr-FR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-300 mb-1">Sujet:</p>
                          <p className="text-white">{msg.subject}</p>
                        </div>
                        
                        <div className="mb-3">
                          <p className="text-sm font-semibold text-gray-300 mb-1">Message:</p>
                          <p className="text-gray-300 whitespace-pre-wrap">{msg.message}</p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2 items-center justify-between w-full">
                          <div className="flex flex-wrap gap-2">
                            {msg.status !== 'in_progress' && (
                              <Button
                                size="sm"
                                onClick={() => handleUpdateMessageStatus(msg.id, 'in_progress')}
                                className="bg-yellow-600 hover:bg-yellow-700"
                              >
                                Marquer en cours
                              </Button>
                            )}
                            {msg.status !== 'completed' && (
                              <Button
                                size="sm"
                                onClick={() => handleUpdateMessageStatus(msg.id, 'completed')}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                Marquer comme traité
                              </Button>
                            )}
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setContactMessageDeleteId(msg.id)}
                            className="border-red-600/60 text-red-400 hover:bg-red-950/50 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4 mr-2 shrink-0" />
                            Supprimer
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="totemium" className="mt-0 space-y-4 focus-visible:outline-none">
            <Card className="bg-[#1A2332] border-[#2D3748]">
              <CardHeader>
                <CardTitle className="text-white">Totemium AI Chat Agent</CardTitle>
                <CardDescription className="text-gray-400">
                  Configure the AI chat widget that replaces the WhatsApp button on the homepage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* API Base URL */}
                <div className="space-y-2">
                  <Label className="text-white">API Base URL</Label>
                  <Input
                    type="text"
                    placeholder="https://totemium.com/api"
                    value={totemiumConfig.api_base_url}
                    onChange={(e) => setTotemiumConfig({ ...totemiumConfig, api_base_url: e.target.value })}
                    className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                  />
                  <p className="text-xs text-gray-400">
                    L'URL de base de l'API Totemium (ex: https://totemium.com/api)
                  </p>
                </div>

                {/* API Key */}
                <div className="space-y-2">
                  <Label className="text-white">Clé / Token</Label>
                  <Input
                    type="password"
                    placeholder="xK7mP9nQ2rT5vW8y..."
                    value={totemiumConfig.api_key}
                    onChange={(e) => setTotemiumConfig({ ...totemiumConfig, api_key: e.target.value })}
                    className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                  />
                  <p className="text-xs text-gray-400">
                    Votre clé API / token Totemium depuis <a href="https://totemium.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">votre espace Totemium</a>
                  </p>
                </div>

                {/* Load Agents Button */}
                <div className="space-y-2">
                  <Button
                    onClick={handleLoadTotemiumAgents}
                    disabled={!totemiumConfig.api_base_url || !totemiumConfig.api_key || loadingAgents}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {loadingAgents ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading agents...</>
                    ) : (
                      <><Bot className="w-4 h-4 mr-2" /> Load Available Agents</>
                    )}
                  </Button>
                </div>

                {/* Chat Agent Selection */}
                {totemiumAgents.length > 0 && (
                  <div className="space-y-3">
                    <Label className="text-white">Select Chat Agent (for website chat widget)</Label>
                    <div className="space-y-2">
                      {totemiumAgents.map((agent) => (
                        <div
                          key={agent.id}
                          onClick={() => handleSelectTotemiumAgent(agent)}
                          className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                            totemiumConfig.agent_id === agent.id
                              ? 'bg-purple-900/30 border-purple-500'
                              : 'bg-[#0F1729] border-[#2D3748] hover:border-purple-500/50'
                          }`}
                        >
                          {agent.icon_url ? (
                            <img src={agent.icon_url} alt={agent.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center">
                              <Bot className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-white font-semibold">{agent.name}</p>
                            {agent.subtitle && <p className="text-gray-400 text-sm">{agent.subtitle}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            {agent.is_active ? (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-green-900/50 text-green-300 border border-green-700">Active</span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-red-900/50 text-red-300 border border-red-700">Inactive</span>
                            )}
                            {totemiumConfig.agent_id === agent.id && (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-purple-900/50 text-purple-300 border border-purple-700">Selected</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Translator Agent Selection */}
                {totemiumAgents.length > 0 && (
                  <div className="space-y-3">
                    <Label className="text-white">Select Translator Agent (for auto-translating offers)</Label>
                    <div className="space-y-2">
                      {totemiumAgents.map((agent) => (
                        <div
                          key={`translator-${agent.id}`}
                          onClick={() => handleSelectTranslatorAgent(agent)}
                          className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                            totemiumConfig.translator_agent_id === agent.id
                              ? 'bg-blue-900/30 border-blue-500'
                              : 'bg-[#0F1729] border-[#2D3748] hover:border-blue-500/50'
                          }`}
                        >
                          {agent.icon_url ? (
                            <img src={agent.icon_url} alt={agent.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                              <Bot className="w-5 h-5 text-white" />
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-white font-semibold">{agent.name}</p>
                            {agent.subtitle && <p className="text-gray-400 text-sm">{agent.subtitle}</p>}
                          </div>
                          <div className="flex items-center gap-2">
                            {agent.is_active ? (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-green-900/50 text-green-300 border border-green-700">Active</span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-red-900/50 text-red-300 border border-red-700">Inactive</span>
                            )}
                            {totemiumConfig.translator_agent_id === agent.id && (
                              <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-900/50 text-blue-300 border border-blue-700">Selected</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Current Selection Summary */}
                {(totemiumConfig.agent_id || totemiumConfig.translator_agent_id) && (
                  <div className="space-y-2">
                    {totemiumConfig.agent_id && (
                      <div className="p-4 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                        <p className="text-purple-300 text-sm">
                          <strong>Chat Agent:</strong> {totemiumConfig.agent_name || `ID ${totemiumConfig.agent_id}`}
                          {totemiumConfig.agent_icon && (
                            <img src={totemiumConfig.agent_icon} alt="" className="inline w-5 h-5 rounded-full ml-2" />
                          )}
                        </p>
                      </div>
                    )}
                    {totemiumConfig.translator_agent_id && (
                      <div className="p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                        <p className="text-blue-300 text-sm">
                          <strong>Translator Agent:</strong> {totemiumConfig.translator_agent_name || `ID ${totemiumConfig.translator_agent_id}`}
                          {totemiumConfig.translator_agent_icon && (
                            <img src={totemiumConfig.translator_agent_icon} alt="" className="inline w-5 h-5 rounded-full ml-2" />
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Save Button */}
                <Button
                  onClick={handleSaveTotemiumConfig}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  Save Totemium Configuration
                </Button>

                {/* Help */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="text-blue-400 font-semibold mb-2">🤖 How it works</h4>
                  <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                    <li>Enter your Totemium API key</li>
                    <li>Click "Load Available Agents" to see your agents</li>
                    <li>Select the agent you want to use for the chat widget</li>
                    <li>Click "Save" — the chat widget on the homepage will use this agent</li>
                    <li>Visitors click the chat bubble (bottom-right) to talk to your AI agent</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hero" className="mt-0 space-y-4 focus-visible:outline-none">
            <Card className="bg-[#1A2332] border-[#2D3748]">
              <CardHeader>
                <CardTitle className="text-white">Média de la section Hero</CardTitle>
                <CardDescription className="text-gray-400">
                  Choisissez entre une image de fond ou une vidéo YouTube en lecture automatique
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Type selector */}
                <div className="space-y-2">
                  <Label className="text-white">Type de média</Label>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setHeroMedia({ ...heroMedia, type: 'image' })}
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                        heroMedia.type === 'image'
                          ? 'border-blue-500 bg-blue-900/30 text-blue-300'
                          : 'border-[#2D3748] bg-[#0F1729] text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <Image className="w-5 h-5" />
                      Image
                    </button>
                    <button
                      onClick={() => setHeroMedia({ ...heroMedia, type: 'youtube' })}
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                        heroMedia.type === 'youtube'
                          ? 'border-red-500 bg-red-900/30 text-red-300'
                          : 'border-[#2D3748] bg-[#0F1729] text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      <Youtube className="w-5 h-5" />
                      Vidéo YouTube
                    </button>
                  </div>
                </div>

                {/* Value input */}
                {heroMedia.type === 'image' ? (
                  <div className="space-y-2">
                    <Label className="text-white">URL de l'image</Label>
                    <Input
                      type="text"
                      placeholder="/images/Doqueve.jpg"
                      value={heroMedia.image_url || ''}
                      onChange={(e) => setHeroMedia({ ...heroMedia, image_url: e.target.value })}
                      className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                    />
                    <p className="text-xs text-gray-400">
                      Chemin relatif (ex: /images/Doqueve.jpg) ou URL absolue
                    </p>
                    {heroMedia.image_url && (
                      <div className="mt-3 rounded-lg overflow-hidden border border-[#2D3748]" style={{height: '160px'}}>
                        <img src={heroMedia.image_url} alt="Preview" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* YouTube URL */}
                    <div className="space-y-2">
                      <Label className="text-white">URL ou ID YouTube</Label>
                      <Input
                        type="text"
                        placeholder="https://youtu.be/M6RB1nWXC8o ou M6RB1nWXC8o"
                        value={heroMedia.youtube_url || ''}
                        onChange={(e) => setHeroMedia({ ...heroMedia, youtube_url: e.target.value })}
                        className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                      />
                      <p className="text-xs text-gray-400">
                        Collez l'URL YouTube complète ou juste l'ID de la vidéo (11 caractères)
                      </p>
                      {heroMedia.youtube_url && (
                        <div className="mt-3 rounded-lg overflow-hidden border border-[#2D3748]" style={{height: '160px'}}>
                          <img
                            src={`https://img.youtube.com/vi/${heroMedia.youtube_url.match(/(?:youtu\.be\/|v=)([a-zA-Z0-9_-]{11})/)?.[1] || heroMedia.youtube_url}/hqdefault.jpg`}
                            alt="YouTube preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>

                    {/* Start / End time */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-white">Start Time <span className="text-gray-400 font-normal">(secondes)</span></Label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="0"
                          value={heroMedia.start_time ?? ''}
                          onChange={(e) => setHeroMedia({ ...heroMedia, start_time: e.target.value === '' ? null : parseInt(e.target.value) })}
                          className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                        />
                        <p className="text-xs text-gray-400">Seconde de début (laisser vide = 0)</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white">End Time <span className="text-gray-400 font-normal">(secondes)</span></Label>
                        <Input
                          type="number"
                          min="0"
                          placeholder="—"
                          value={heroMedia.end_time ?? ''}
                          onChange={(e) => setHeroMedia({ ...heroMedia, end_time: e.target.value === '' ? null : parseInt(e.target.value) })}
                          className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                        />
                        <p className="text-xs text-gray-400">Seconde de fin (laisser vide = fin naturelle)</p>
                      </div>
                    </div>

                    {/* Play Once toggle */}
                    <div className="flex items-center justify-between p-4 bg-[#0B1120] border border-[#2D3748] rounded-lg">
                      <div>
                        <p className="text-white font-medium">Play Once</p>
                        <p className="text-xs text-gray-400">Jouer une seule fois (désactive la boucle)</p>
                      </div>
                      <button
                        onClick={() => setHeroMedia({ ...heroMedia, play_once: !heroMedia.play_once })}
                        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                          heroMedia.play_once ? 'bg-red-500' : 'bg-[#2D3748]'
                        }`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          heroMedia.play_once ? 'translate-x-8' : 'translate-x-1'
                        }`} />
                        <span className={`absolute text-xs font-bold ${heroMedia.play_once ? 'left-2 text-white' : 'right-2 text-gray-400'}`}>
                          {heroMedia.play_once ? 'Yes' : 'No'}
                        </span>
                      </button>
                    </div>

                    {/* Play on Mobile toggle */}
                    <div className="flex items-center justify-between p-4 bg-[#0B1120] border border-[#2D3748] rounded-lg">
                      <div>
                        <p className="text-white font-medium">Play On Mobile</p>
                        <p className="text-xs text-gray-400">Afficher la vidéo sur mobile (sinon → image de fallback)</p>
                      </div>
                      <button
                        onClick={() => setHeroMedia({ ...heroMedia, play_on_mobile: heroMedia.play_on_mobile === false ? true : false })}
                        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                          heroMedia.play_on_mobile !== false ? 'bg-red-500' : 'bg-[#2D3748]'
                        }`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                          heroMedia.play_on_mobile !== false ? 'translate-x-8' : 'translate-x-1'
                        }`} />
                        <span className={`absolute text-xs font-bold ${heroMedia.play_on_mobile !== false ? 'left-2 text-white' : 'right-2 text-gray-400'}`}>
                          {heroMedia.play_on_mobile !== false ? 'Yes' : 'No'}
                        </span>
                      </button>
                    </div>

                    {/* Fallback image */}
                    <div className="space-y-2">
                      <Label className="text-white">Image de fallback</Label>
                      <Input
                        type="text"
                        placeholder="/images/Doqueve.jpg"
                        value={heroMedia.fallback_image || ''}
                        onChange={(e) => setHeroMedia({ ...heroMedia, fallback_image: e.target.value })}
                        className="bg-[#0B1120] border-[#2D3748] text-white placeholder:text-gray-500"
                      />
                      <p className="text-xs text-gray-400">
                        Image affichée sur mobile (si Play On Mobile = No) ou en cas d'erreur vidéo
                      </p>
                      {heroMedia.fallback_image && (
                        <div className="mt-2 rounded-lg overflow-hidden border border-[#2D3748]" style={{height: '120px'}}>
                          <img src={heroMedia.fallback_image} alt="Fallback preview" className="w-full h-full object-cover" />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSaveHeroMedia}
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  Sauvegarder le média Hero
                </Button>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="text-blue-400 font-semibold mb-2">ℹ️ Comment ça marche</h4>
                  <ul className="text-sm text-gray-300 space-y-1 list-disc list-inside">
                    <li><strong className="text-white">Image :</strong> affiche une photo en plein écran</li>
                    <li><strong className="text-white">Vidéo YouTube :</strong> autoplay muet, démarre/se termine aux secondes configurées</li>
                    <li><strong className="text-white">Play Once :</strong> joue une fois puis s'arrête (sinon boucle infinie)</li>
                    <li><strong className="text-white">Play On Mobile :</strong> désactiver pour montrer l'image de fallback sur mobile</li>
                    <li><strong className="text-white">Fallback :</strong> image affichée si vidéo désactivée sur mobile</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="promo-bar" className="mt-0 space-y-4 focus-visible:outline-none">
            <AdminPromoBarTab
              apiUrl={API_URL}
              token={token}
              config={promoBarConfig}
              onChange={setPromoBarConfig}
            />
          </TabsContent>

          <TabsContent value="matomo" className="mt-0 space-y-4 focus-visible:outline-none">
            <AdminMatomoTab
              apiUrl={API_URL}
              token={token}
              config={matomoConfig}
              onChange={setMatomoConfig}
            />
          </TabsContent>

          <TabsContent value="google-analytics" className="mt-0 space-y-4 focus-visible:outline-none">
            <AdminGoogleAnalyticsTab
              apiUrl={API_URL}
              token={token}
              config={googleAnalyticsConfig}
              onChange={setGoogleAnalyticsConfig}
            />
          </TabsContent>

          </main>
        </div>
      </Tabs>

      <AlertDialog open={contactMessageDeleteId !== null} onOpenChange={(open) => !open && setContactMessageDeleteId(null)}>
        <AlertDialogContent className="bg-[#1A2332] border-[#2D3748] text-white sm:rounded-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer ce message ?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Cette action est définitive. Le message sera retiré de la liste.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#2D3748] bg-transparent text-gray-300 hover:bg-[#2D3748] hover:text-white">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={(e) => {
                e.preventDefault();
                if (contactMessageDeleteId) void handleDeleteContactMessage(contactMessageDeleteId);
              }}
            >
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <OfferTranslationModal
        offer={selectedOffer}
        isOpen={isTranslationModalOpen}
        onClose={closeTranslationModal}
        onSave={handleTranslationSave}
      />
    </div>
  );
};

export default AdminPanel;
